import * as React from 'react';
import { Plus, RefreshCw } from 'lucide-react';
import { DashboardTableSearchInput } from '@shigo-ui/dashboard-ui';
import { AutocompleteFilter, Button } from '@shigo-ui/ui';

import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { dashboardT } from '../../../../../lib/messages';

type BrandsTableToolbarProps = {
  showAgencyFilter: boolean;
  agencyIds: readonly string[];
  onAgenciesChange: (agencyIds: string[]) => void;
  search: string;
  onSearchChange: (value: string) => void;
  industryOptions: readonly string[];
  selectedIndustries: readonly string[];
  industriesLoading: boolean;
  onIndustriesChange: (industries: string[]) => void;
  onRefresh: () => void;
  onCreate: (() => void) | undefined;
};

export function BrandsTableToolbar({
  showAgencyFilter,
  agencyIds,
  onAgenciesChange,
  search,
  onSearchChange,
  industryOptions,
  selectedIndustries,
  industriesLoading,
  onIndustriesChange,
  onRefresh,
  onCreate,
}: BrandsTableToolbarProps) {
  return (
    <>
      {showAgencyFilter ? (
        <AutocompleteFilter
          label={dashboardT('agencyAssignment.filter')}
          options={[]}
          selectedIds={[...agencyIds]}
          onSelectedIdsChange={onAgenciesChange}
          selectAll={false}
          emptyMessage={dashboardT('agencyWorkspace.unavailable')}
          className="hidden"
        />
      ) : null}
      <BrandsSearchInput key={search} initialValue={search} onChange={onSearchChange} />
      <AutocompleteFilter
        label={dashboardT('brands.allIndustries')}
        options={industryOptions.map((industry) => ({ id: industry, label: industry }))}
        selectedIds={[...selectedIndustries]}
        onSelectedIdsChange={onIndustriesChange}
        loading={industriesLoading}
        selectAll={false}
        className="sm:w-62"
      />
      <Button
        variant="outline"
        size="icon"
        aria-label={dashboardT('brands.refresh')}
        disabled={agencyIds.length > 0}
        onClick={onRefresh}
      >
        <RefreshCw className="h-4 w-4" aria-hidden="true" />
      </Button>
      {onCreate ? (
        <Button onClick={onCreate}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          {dashboardT('brands.create')}
        </Button>
      ) : null}
    </>
  );
}

function BrandsSearchInput({
  initialValue,
  onChange,
}: {
  initialValue: string;
  onChange: (value: string) => void;
}) {
  // Emits only typed changes, so mounting (or leaving while a route loads) never navigates.
  const search = useDebouncedValue(initialValue, onChange, 650);

  return (
    <DashboardTableSearchInput
      value={search.draft}
      aria-label={dashboardT('brands.search')}
      placeholder={dashboardT('brands.searchPlaceholder')}
      onChange={(event) => {
        search.setDraft(event.target.value);
      }}
    />
  );
}
